import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verify, resendCode } from '@/features/auth/services/authApi';

const RESEND_COOLDOWN = 5 * 60; // 5 minutes in seconds

export default function VerifyPage() {
  const location = useLocation();
  const emailFromState = (location.state as { email?: string })?.email || '';

  const [email, setEmail] = useState(emailFromState);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (emailFromState) codeRef.current?.focus();
  }, [emailFromState]);

  const startCooldown = useCallback((seconds: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCooldown(seconds);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    // Start cooldown immediately if user just registered (code was just sent)
    if (emailFromState) {
      startCooldown(RESEND_COOLDOWN);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [emailFromState, startCooldown]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await verify(email, code);
      navigate('/login', { state: { verified: true } });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email || cooldown > 0 || resending) return;
    setError('');
    setMessage('');
    setResending(true);
    try {
      const result = await resendCode(email);
      setMessage(result.message);
      startCooldown(RESEND_COOLDOWN);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { error?: string; retry_after?: number } } })?.response;
      if (resp?.status === 429 && resp.data) {
        if (resp.data.retry_after) {
          startCooldown(resp.data.retry_after);
        }
        setError(resp.data.error || 'Please wait before requesting a new code.');
      } else {
        setError('Failed to resend code.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-bold mb-2 text-accent">Verify Your Email</h1>
        <p className="text-sm text-muted mb-6">
          Enter the 6-digit code sent to your email.
        </p>
        <hr className="mb-6" />

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
        {message && <p className="text-sm text-muted mb-4">{message}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-xs font-bold mb-1">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label htmlFor="code" className="block text-xs font-bold mb-1">Verification Code</label>
            <input
              id="code"
              ref={codeRef}
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={6}
              pattern="[0-9]{6}"
              placeholder="000000"
              className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent text-center tracking-widest text-lg"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-terminal disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        <button
          onClick={handleResend}
          disabled={cooldown > 0 || resending}
          className="mt-4 text-sm text-accent hover:underline bg-transparent border-none cursor-pointer font-mono p-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
        >
          {resending
            ? 'Sending...'
            : cooldown > 0
              ? `Resend code (${formatTime(cooldown)})`
              : 'Resend code'}
        </button>
      </div>
    </div>
  );
}
